import { useEffect, useMemo } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import {
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  format,
  parseISO,
} from "date-fns"

import { createActivityCommonStore, createActivityStore } from "@/app/activity/store"
import type { ActivityViewMode } from "@/app/activity/store/activityCommonStore"
import ActivityHeader from "@/app/activity/list/activityHeader/ActivityHeader"
import ActivityMonthView from "@/app/activity/list/views/ActivityMonthView"
import ActivityWeekView from "@/app/activity/list/views/ActivityWeekView"
import ActivityScheduleView from "@/app/activity/list/views/ActivityScheduleView"
import ActivityInfoDialog from "@/app/activity/list/activityInfoDialog/ActivityInfoDialog"
import UpsertActivityDialog from "@/app/activity/components/upsertActivityDialog/UpsertActivityDialog"
import type { Activity } from "@/types/activity"

import "./activityListPage.scss"

export default function ActivityListPage() {
  const navigate = useNavigate({ from: "/activity/list" })
  const search = useSearch({ from: "/activity/list" })

  const {
    viewMode,
    setViewMode,
    currentDate,
    setCurrentDate,
    setSelectedDate,
    filters,
    setFilters,
    infoDialogActivity,
    setInfoDialogActivity,
  } = createActivityCommonStore((state) => ({
    viewMode: state.viewMode,
    setViewMode: state.setViewMode,
    currentDate: state.currentDate,
    setCurrentDate: state.setCurrentDate,
    setSelectedDate: state.setSelectedDate,
    filters: state.filters,
    setFilters: state.setFilters,
    infoDialogActivity: state.infoDialogActivity,
    setInfoDialogActivity: state.setInfoDialogActivity,
  }))

  const { isUpsertMode, setIsUpsertMode, setActivity, setTempActivity } = createActivityStore(
    (state) => ({
      isUpsertMode: state.isUpsertMode,
      setIsUpsertMode: state.setUpsertMode,
      setActivity: state.setActivity,
      setTempActivity: state.setTempActivity,
    }),
  )

  // URL → store 초기 동기화 (1회)
  useEffect(() => {
    if (search.view) {
      setViewMode(search.view)
    }
    if (search.date) {
      const d = parseISO(search.date)
      if (!Number.isNaN(d.getTime())) {
        setCurrentDate(d)
      }
    }
    if (search.types) {
      const types = search.types.split(",").filter(Boolean)
      setFilters({ types: types as never[] })
    }
    setFilters({
      startOnMonday: search.startMon === 1,
      alignToMatchDay: search.alignMd === 1,
    })
  }, [])

  // store → URL 동기화 — navigate 를 의존성에서 제거 (useNavigate 가 매 렌더 새 함수를 반환할 수 있어 루프 방지)
  useEffect(() => {
    navigate({
      search: (prev) => ({
        ...prev,
        view: viewMode,
        date: format(currentDate, "yyyy-MM-dd"),
        types: filters.types.length ? filters.types.join(",") : undefined,
        startMon: filters.startOnMonday ? 1 : undefined,
        alignMd: filters.alignToMatchDay ? 1 : undefined,
      }),
      replace: true,
    })
  }, [viewMode, currentDate, filters.types, filters.startOnMonday, filters.alignToMatchDay])

  // BodyContentCont 의 padding/shadow 제거 (캘린더 풀스크린 레이아웃)
  useEffect(() => {
    const bodyContainer = document.querySelector(".BodyContentCont")
    bodyContainer?.classList.add("activity-list-page-mode")

    return () => {
      bodyContainer?.classList.remove("activity-list-page-mode")
    }
  }, [])

  const handleNewActivity = () => {
    setTempActivity(undefined)
    setActivity(undefined)
    setIsUpsertMode(true)
  }

  const handleToday = () => {
    setCurrentDate(new Date())
  }

  const handlePrev = () => {
    if (viewMode === "week") {
      setCurrentDate(subWeeks(currentDate, 1))
    } else if (viewMode === "schedule") {
      setCurrentDate(subDays(currentDate, 1))
    } else {
      setCurrentDate(subMonths(currentDate, 1))
    }
  }

  const handleNext = () => {
    if (viewMode === "week") {
      setCurrentDate(addWeeks(currentDate, 1))
    } else if (viewMode === "schedule") {
      setCurrentDate(addDays(currentDate, 1))
    } else {
      setCurrentDate(addMonths(currentDate, 1))
    }
  }

  const handleJumpToDate = (date: Date) => {
    setCurrentDate(date)
    setSelectedDate(date)
  }

  const view = useMemo(() => {
    switch (viewMode as ActivityViewMode) {
      case "week":
        return <ActivityWeekView />
      case "schedule":
        return <ActivityScheduleView />
      case "month":
      default:
        return <ActivityMonthView />
    }
  }, [viewMode])

  return (
    <div className="ActivityListPageCont">
      <ActivityHeader
        onNewActivity={handleNewActivity}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        onJumpToDate={handleJumpToDate}
      />
      <div className="ActivityListPage__body">{view}</div>
      {isUpsertMode && (
        <UpsertActivityDialog
          open
          onClose={() => {
            setIsUpsertMode(false)
            setActivity(undefined)
            setTempActivity(undefined)
          }}
        />
      )}
      {infoDialogActivity && (
        <ActivityInfoDialog
          open
          activity={infoDialogActivity}
          onClose={() => setInfoDialogActivity(null)}
          onEdit={(activity: Activity) => {
            setInfoDialogActivity(null)
            setActivity(activity)
            setTempActivity(activity)
            setIsUpsertMode(true)
          }}
        />
      )}
    </div>
  )
}
